import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/components/ui/use-toast'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
	Star,
	Mail,
	Link2 as LinkIconLucide, // Renamed to avoid conflict with <a>
	Briefcase, // Keep if reporter_company is still relevant (not in target image but good to have)
	CheckCircle,
	AlertTriangle,
	MessageSquare, // Used for program icon in existing, not in target
	Clock,
	// Edit3, // Used for widget icon in existing, not in target
	// CalendarDays, // Used for submitted icon in existing, not in target
	Laptop,
	Smartphone, // From existing UserMetadataDisplay, will use Laptop for 'desktop'
	Globe as GlobeIcon,
	Monitor, // Could be an alternative for OS icon
	Cpu,
	UserCircle, // For social media icon
	Copy, // New
	ArrowRight, // New
	ExternalLink, // New
	Check, // New for "Approved" badge
	X as XIcon,
	Link2, // For Dialog Close if not automatically handled by shadcn
} from 'lucide-react'
import { format, formatDistanceToNow } from 'date-fns'
import { cn } from '@/lib/utils'
import {
	extractTwitterUsername,
	getTwitterProfileImage,
	getInitials,
} from '@/utils/reviews' // Assuming these utils are correct
import {
	ReviewDetail,
	ReviewDetailViewProps,
	UserMetadata,
} from '@/types/reviews.types'
import { useTranslation } from '@/hooks/useTranslation'

const StarRatingDisplay = ({ score, t }: { score: number | null; t: (key: string) => string }) => {
	if (score === null)
		return <span className="text-sm text-muted-foreground">{t('reviewDetailView.noRating')}</span>
	return (
		<div className="flex items-center">
			<div className="flex items-center gap-0.5">
				{[1, 2, 3, 4, 5].map((star) => (
					<Star
						key={star}
						className={cn(
							'w-5 h-5', // Target image stars look about this size
							star <= score
								? 'text-yellow-400 fill-yellow-400'
								: 'text-gray-300 dark:text-gray-500'
						)}
					/>
				))}
			</div>
			<span className="ml-2 text-sm font-medium text-foreground">
				({score}/5)
			</span>
			{/* Sentiment Emoji based on score */}
			{score >= 4 && <span className="ml-1.5 text-lg">😊</span>}
			{score === 3 && <span className="ml-1.5 text-lg">🙂</span>}
			{score < 3 && score > 0 && <span className="ml-1.5 text-lg">😕</span>}
		</div>
	)
}

// No longer using the old UserMetadataDisplay directly, but will use its data
// const UserMetadataDisplay = ...

export const ReviewDetailView: React.FC<ReviewDetailViewProps> = ({
	reviewId,
	onClose,
	isOpen,
}) => {
	const { t } = useTranslation()
	const { toast } = useToast()
	const queryClient = useQueryClient()

	const {
		data: review,
		isLoading,
		error,
		refetch,
	} = useQuery<ReviewDetail, Error>({
		queryKey: ['review', reviewId],
		queryFn: async () => {
			if (!reviewId) throw new Error('Review ID is required')
			const { data: rawData, error: dbError } = await supabase
				.from('reviews')
				.select(
					`id, reporter_name, reporter_email, reporter_company, review_text, score, created_at, updated_at, approved_at, publish_status, widget_id, program_id, response_id, is_verified, email, name, social_media_link, consent_given, widget:widgets(id, name), program:programs(id, name), response:responses(id, user_metadata)`
				)
				.eq('id', reviewId)
				.single()
			if (dbError) throw dbError
			if (!rawData) throw new Error('Review not found.')

			const reviewData: ReviewDetail = {
				...(rawData as Omit<typeof rawData, 'widget' | 'program' | 'response'>),
				widget: Array.isArray(rawData.widget)
					? rawData.widget[0] || null
					: rawData.widget || null,
				program: Array.isArray(rawData.program)
					? rawData.program[0] || null
					: rawData.program || null,
				response: Array.isArray(rawData.response)
					? rawData.response[0] || null
					: rawData.response || null,
				twitterProfileImage: undefined,
			}

			if (reviewData.social_media_link) {
				const username = extractTwitterUsername(reviewData.social_media_link)
				if (username) {
					reviewData.twitterProfileImage = getTwitterProfileImage(username) // This is an async call in reality, ensure it's handled or pre-fetched if it's a URL
				}
			}
			return reviewData
		},
		enabled: !!reviewId && isOpen,
	})

	useEffect(() => {
		if (isOpen && reviewId) {
			refetch()
		}
	}, [isOpen, reviewId, refetch])

	const handleCopy = (text: string | undefined, fieldName: string) => {
		if (!text) return
		navigator.clipboard
			.writeText(text)
			.then(() => {
				toast({
					title: t('reviewDetailView.copiedToClipboard', { fieldName }),
					variant: 'default',
					duration: 2000,
				})
			})
			.catch((err) => {
				toast({
					title: t('reviewDetailView.failedToCopy', { fieldName }),
					description: err.message,
					variant: 'destructive',
				})
			})
	}

	const displayName = review?.name || review?.reporter_name || t('bugReportTable.anonymous')
	const displayEmail = review?.email || review?.reporter_email
	const socialHandle = review?.social_media_link
		? extractTwitterUsername(review.social_media_link)
		: null

	const renderContent = () => {
		if (isLoading) {
			return (
				<div className="p-6 space-y-6 bg-slate-50 dark:bg-slate-900">
					{/* Header Skeleton */}
					<div className="flex items-center justify-between p-4 border-b">
						<Skeleton className="h-6 w-1/2" />
						<Skeleton className="h-6 w-6 rounded-full" />
					</div>
					{/* Card Skeletons */}
					{[1, 2, 3, 4].map((i) => (
						<div
							key={i}
							className="p-4 bg-white dark:bg-slate-800 rounded-lg shadow">
							<Skeleton className="h-4 w-1/4 mb-3" />
							<Skeleton className="h-3 w-3/4 mb-1" />
							<Skeleton className="h-3 w-1/2" />
						</div>
					))}
				</div>
			)
		}

		if (error) {
			return (
				<div className="p-6 text-center text-destructive bg-slate-50 dark:bg-slate-900">
					<div className="p-4 bg-white dark:bg-slate-800 rounded-lg shadow">
						<AlertTriangle className="h-12 w-12 mx-auto mb-3" />
						<p className="text-lg font-semibold">{t('reviewDetailView.errorLoadingReview')}</p>
						<p className="text-sm">{error.message}</p>
						<Button onClick={() => refetch()} className="mt-4">
							{t('reviewDetailView.tryAgain')}
						</Button>
					</div>
				</div>
			)
		}

		if (!review) {
			return (
				<div className="p-6 text-center text-muted-foreground bg-slate-50 dark:bg-slate-900">
					<div className="p-4 bg-white dark:bg-slate-800 rounded-lg shadow">
						<MessageSquare className="h-12 w-12 mx-auto mb-3" />
						<p>{t('reviewDetailView.noReviewDataFound')}</p>
					</div>
				</div>
			)
		}

		const userMeta = review.response?.user_metadata as UserMetadata | undefined

		return (
			<>
				<DialogHeader className="p-4 border-b dark:border-slate-700 flex-row items-center justify-between sticky top-0 bg-white dark:bg-slate-800 z-10">
					<DialogTitle className="text-lg font-semibold text-foreground flex items-center">
						{t('reviewDetailView.reviewDetails')}
					</DialogTitle>
					<button
						onClick={onClose}
						aria-label={t('reviewDetailView.closeDialog')}
						className="p-1 rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
						<XIcon className="w-5 h-5" />
					</button>
				</DialogHeader>

				<div className="p-4 sm:p-5 space-y-4 bg-slate-50 dark:bg-slate-950 overflow-y-auto flex-grow">
					{/* Reviewer Card */}
					<div className="bg-white dark:bg-slate-900 p-4 rounded-lg shadow-sm border dark:border-slate-700/50">
						<div className="flex items-start gap-3">
							<Avatar className="w-10 h-10 border dark:border-slate-600">
								{review.twitterProfileImage ? (
									<AvatarImage
										src={review.twitterProfileImage}
										alt={displayName}
									/>
								) : (
									<AvatarFallback className="text-sm bg-slate-100 dark:bg-slate-700">
										{getInitials(displayName)}
									</AvatarFallback>
								)}
							</Avatar>
							<div className="flex-grow min-w-0">
								<p className="font-medium text-sm truncate text-foreground">
									{displayName}
								</p>
								{displayEmail && (
									<div className="flex items-center text-xs text-muted-foreground group">
										<Mail className="w-3.5 h-3.5 mr-1.5 text-slate-400 dark:text-slate-500" />
										<span className="truncate">{displayEmail}</span>
										<button
											onClick={() => handleCopy(displayEmail, t('reviewDetailView.email'))}
											className="ml-1.5 opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100"
											aria-label={t('reviewDetailView.copyEmail')}>
											<Copy className="w-3 h-3 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300" />
										</button>
									</div>
								)}
								{socialHandle && (
									<div className="flex items-center text-xs text-muted-foreground group mt-0.5">
										<UserCircle className="w-3.5 h-3.5 mr-1.5 text-slate-400 dark:text-slate-500" />
										<a
											href={review.social_media_link || '#'}
											target="_blank"
											rel="noopener noreferrer"
											className="truncate hover:underline text-blue-600 dark:text-blue-400">
											@{socialHandle}
										</a>
										<button
											onClick={() =>
												handleCopy(`@${socialHandle}`, t('reviewDetailView.socialHandle'))
											}
											className="ml-1.5 opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100"
											aria-label={t('reviewDetailView.copySocialHandle')}>
											<Copy className="w-3 h-3 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300" />
										</button>
									</div>
								)}
								{review.reporter_company && (
									<div className="flex items-center text-xs text-muted-foreground mt-0.5">
										<Briefcase className="w-3.5 h-3.5 mr-1.5 text-slate-400 dark:text-slate-500" />
										<span className="truncate">{review.reporter_company}</span>
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="bg-white dark:bg-slate-900 p-4 rounded-lg shadow-sm border dark:border-slate-700/50">
						<StarRatingDisplay score={review.score} t={t} />
						<p
							className="mt-2.5 text-sm leading-relaxed whitespace-pre-line text-foreground"
							style={{ wordBreak: 'break-word' }}>
							{review.review_text || (
								<span className="italic text-muted-foreground">
									{t('reviewDetailView.noReviewTextProvided')}
								</span>
							)}
						</p>
						<div className="mt-3 space-y-1">
							<div className="flex items-center text-xs text-muted-foreground">
								<Clock className="w-3.5 h-3.5 mr-1.5" />
								{t('reviewDetailView.submitted')}:{' '}
								{format(
									new Date(review.created_at),
									"MMM d, yyyy 'at' h:mm a"
								)}{' '}
								(
								{formatDistanceToNow(new Date(review.created_at), {
									addSuffix: true,
								})}
								)
							</div>
							{review.updated_at &&
								new Date(review.updated_at).getTime() !==
									new Date(review.created_at).getTime() && (
									<div className="flex items-center text-xs text-muted-foreground">
										<Clock className="w-3.5 h-3.5 mr-1.5" />
										{t('reviewDetailView.lastUpdated')}:{' '}
										{format(
											new Date(review.updated_at),
											"MMM d, yyyy 'at' h:mm a"
										)}{' '}
										(
										{formatDistanceToNow(new Date(review.updated_at), {
											addSuffix: true,
										})}
										)
									</div>
								)}
						</div>
					</div>

					<div className="bg-white dark:bg-slate-900 p-4 rounded-lg shadow-sm border dark:border-slate-700/50">
						<h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
							{t('reviewDetailView.statusAndSettings')}
						</h3>
						<div className="space-y-2 text-sm">
							<div className="flex items-center justify-between">
								<span className="text-foreground">{t('reviewDetailView.verificationStatus')}:</span>
								{review.is_verified ? (
									<Badge
										variant="outline"
										className="text-xs border-green-500 text-green-700 bg-green-50 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700">
										<CheckCircle className="w-3 h-3 mr-1" /> {t('reviewDetailView.verified')}
									</Badge>
								) : (
									<Badge
										variant="outline"
										className="text-xs border-yellow-500 text-yellow-700 bg-yellow-50 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-700">
										<AlertTriangle className="w-3 h-3 mr-1" /> {t('reviewDetailView.notVerified')}
									</Badge>
								)}
							</div>
							<div className="flex items-center justify-between">
								<span className="text-foreground">{t('reviewDetailView.publicDisplayConsent')}:</span>
								{review.consent_given === true ? (
									<Badge
										variant="outline"
										className="text-xs border-green-500 text-green-700 bg-green-50 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700">
										<Check className="w-3 h-3 mr-1" /> {t('reviewDetailView.approved')}
									</Badge>
								) : review.consent_given === false ? (
									<Badge
										variant="outline"
										className="text-xs border-red-500 text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700">
										<XIcon className="w-3 h-3 mr-1" /> {t('reviewDetailView.notApproved')}
									</Badge>
								) : (
									<span className="text-xs italic text-muted-foreground">
										{t('reviewDetailView.notSpecified')}
									</span>
								)}
							</div>
						</div>
					</div>

					<div className="bg-white dark:bg-slate-900 p-4 rounded-lg shadow-sm border dark:border-slate-700/50">
						<h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
							{t('reviewDetailView.sourceAndEnvironment')}
						</h3>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
							<div>
								<h4 className="text-xs font-medium text-muted-foreground mb-1.5">
									{t('reviewDetailView.sourceDetails')}
								</h4>
								{review.widget?.name && (
									<div className="flex items-center justify-between mb-1">
										<span className="text-foreground w-32 shrink-0">
											{t('reviewDetailView.widget')}:
										</span>
										<a
											href={`/widgets/${review.widget?.id}`}
											className="flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400 truncate font-semibold">
											<span className="truncate">{review.widget.name}</span>
											<Link2 className="w-3 h-3 flex-shrink-0" />
										</a>
									</div>
								)}
								{review.program?.name && (
									<div className="flex items-center justify-between">
										<span className="text-foreground w-32 shrink-0">
											{t('reviewDetailView.program')}:
										</span>
										<div className="flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400 truncate font-semibold">
											<span className="truncate">{review.program.name}</span>
										</div>
									</div>
								)}
							</div>
							<div>
								<h4 className="text-xs font-medium text-muted-foreground mb-1.5">
									{t('reviewDetailView.userEnvironment')}
								</h4>
								{userMeta ? (
									<div className="flex flex-wrap gap-1.5">
										{userMeta.device && (
											<Badge
												variant="secondary"
												className="text-xs font-normal bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
												{userMeta.isMobile ? (
													<Smartphone className="w-3 h-3 mr-1" />
												) : (
													<Laptop className="w-3 h-3 mr-1" />
												)}
												{userMeta.device}
											</Badge>
										)}
										{userMeta.os && (
											<Badge
												variant="secondary"
												className="text-xs font-normal bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
												<Cpu className="w-3 h-3 mr-1" />
												{userMeta.os}
											</Badge>
										)}
										{userMeta.browser && (
											<Badge
												variant="secondary"
												className="text-xs font-normal bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
												<GlobeIcon className="w-3 h-3 mr-1" />
												{userMeta.browser}
											</Badge>
										)}
									</div>
								) : (
									<p className="text-xs text-muted-foreground italic">
										{t('reviewDetailView.noUserEnvironmentData')}
									</p>
								)}
							</div>
						</div>
						{userMeta?.pageUrl && (
							<div className="mt-3 pt-3 border-t dark:border-slate-700/50 flex items-center justify-between text-sm">
								<span className="text-muted-foreground w-32 shrink-0">
									{t('reviewDetailView.submissionUrl')}:
								</span>
								<a
									href={userMeta.pageUrl}
									target="_blank"
									rel="noopener noreferrer"
									className="text-blue-600 hover:underline dark:text-blue-400 flex items-center gap-1 truncate min-w-0">
									<span className="truncate">
										{userMeta.pageUrl.slice(0, 30)}...
									</span>
									<ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
								</a>
							</div>
						)}
					</div>
				</div>
			</>
		)
	}

	return (
		<Dialog
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) onClose()
			}}>
			<DialogContent className="flex flex-col w-full p-0 sm:max-w-lg md:max-w-xl lg:max-w-2xl max-h-[90vh] sm:max-h-[calc(100vh-80px)] rounded-lg overflow-hidden">
				{renderContent()}
			</DialogContent>
		</Dialog>
	)
}
